import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormioComponent } from '@formio/angular';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from '@shared/event.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';

import { DynamicFormService } from './dynamic-form.service';
import { ValidationType } from './validation-type.enum';
import { FormBuilderService } from '@admin/form-manager/form-builder/form-builder.service';
import { FieldsFilledStatus } from '@shared/components/dynamic-form/dynamic-form.types';
import { FormUtils } from '@shared/components/dynamic-form/utils/form-utils';

@Component({
  selector: 'oes-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  providers: [FormBuilderService]
})
export class DynamicFormComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild(FormioComponent, { static: false }) formioComponent: FormioComponent;

  @Input() json: any;
  @Input() readOnly: boolean;
  @Input() simple = false;
  @Input() enableSubmit = false;
  @Input() autoSaveActive = true;
  @Input() disableSaveButton = true;

  @Output() save: EventEmitter<any> = new EventEmitter<any>();
  @Output() invalid: EventEmitter<any> = new EventEmitter<any>();
  @Output() beforeSubmission: EventEmitter<any> = new EventEmitter<any>();
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() formValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() fileSave: EventEmitter<any> = new EventEmitter<any>();
  @Output() requiredFieldsFilledStatus: EventEmitter<FieldsFilledStatus> = new EventEmitter<FieldsFilledStatus>();
  @Output() dataChanged: EventEmitter<any> = new EventEmitter<any>();

  submissionData: any;
  type = ValidationType;
  updatedForm: any;

  private _invalidItems = [];
  private isInitialUpdate = true;
  private isRunningValidation: boolean = false;
  private message = {};
  private ngUnsubscribe: Subject<any> = new Subject();

  private _changeValueSubject = new BehaviorSubject<any>(false);
  private _changeValues$: Observable<any> = this._changeValueSubject.asObservable();
  private _debouncedValidation = new Subject<any>();

  constructor(private _dynamicFormService: DynamicFormService,
              private _eventService: EventService,
              private _formBuilderService: FormBuilderService,
              private _translateService: TranslateService) {
    this.message = this._translateService.instant('general-message');
  }

  ngOnInit() {
    this.exportDefaultValues(this.json);
    if (this.json?.components) {
      // remove old submit button and make a clone
      const cleanedForm = { ...this._dynamicFormService.checkSubmitButton(this.json) };
      const lang = this._translateService.currentLang;
      this.updatedForm = this._formBuilderService.translateComponent(cleanedForm, lang);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    if (!this.readOnly) {
      // formio is ready
      this.formioComponent.ready
        .pipe(take(1))
        .subscribe((formio: FormioComponent) => {

          this.subscribeChanges();
          this.setupDebouncedValidation();

          if (this.formioComponent?.formio) {
            this.formioComponent.formio.on('change', (event) => {
              this._debouncedValidation.next(event);
            });
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.formioComponent?.formio) {
      this.applyDisabledStateToButtons(changes.disableSaveButton?.currentValue);
    }
  }

  /** Setup a debounced validation */
  private setupDebouncedValidation() {
    this._debouncedValidation.pipe(
      debounceTime(3000), // 3 second debounce time
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => {
      this.determineAndSetInvalidItems();
    });
  }

  private checkFormValidity(): boolean {
    const formio = this.formioComponent.formio;
    return formio.checkValidity(formio.submission.data, true);
  }

  private determineAndSetInvalidItems() {
    if (this.checkFormValidity()) {
      this.invalidItems = null;
      this.formValid.emit(true);
      this.requiredFieldsFilledStatus.emit({ requiredFieldsFullyFilled: true, fieldsPartiallyFilled: false });
      return;
    }

    const errors = this.formioComponent.formio.errors;
    this.invalidItems = errors;
    this.formValid.emit(false);

    const hasRequiredFieldErrors = errors.some(error => error.message && error.message.includes('required'));

    const isAllEmpty = FormUtils.isAllFieldsEmpty(this.updatedForm.data);

    this.requiredFieldsFilledStatus.emit({
      requiredFieldsFullyFilled: !hasRequiredFieldErrors,
      fieldsPartiallyFilled: !isAllEmpty
    });
  }

  get invalidItems() {
    return this._invalidItems;
  }

  set invalidItems(value) {
    this._invalidItems = value;
    this.formValid.emit(this.invalidItems?.length ? false : true);
  }

  // Old form doesn't have json.data because we are using defaultValue as a data storage before. Now it stores in json['data']
  // To run a formio mandatory fields validation, we need to use [submission] of formio
  private exportDefaultValues(json) {
    if (json && !json.data) {
      const result = this._dynamicFormService.exportDefaultValues(json);
      if (result) {
        this.submissionData = { data: result };
      }
    } else {
      this.submissionData = { data: json.data };
    }
  }

  private subscribeChanges() {
    this._changeValues$.pipe(
      debounceTime(30000),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(result => {
      const event = this._changeValueSubject.getValue();
      if (event.data) {
        this.updateInvalidItems(event);
        if (this.autoSaveActive) {
          this.save.emit(event);
        }
      }
    });
  }

  public beforeSubmit() {
    if (this.updatedForm) {
      this.beforeSubmission.emit(this.updatedForm);
    }
  }

  public emitEvent(event) {
    if (event.data) {
      if (event.type === 'save' || event.type === 'submit') {
        const newEvent = event;
        newEvent.data = this.removeWhiteSpace(event.data);
        this.updateInvalidItems(event);
        this.save.emit(newEvent);
      } else if (event.type === 'validate') {
        this.updateInvalidItems(event, true);
      }
    }
  }

  public saveTriggered() {
    this.save.emit(this.updatedForm);
  }

  /**
   * Validates the current data now, without waiting for the debounced validation, so a caller can
   * save an accurate completeness flag (DT-6513: a save within 3s of an edit used to carry the stale one).
   */
  public validateNow(): Promise<boolean> {
    if (!this.formioComponent?.formio) {
      return Promise.resolve(false);
    }
    return Promise.resolve(this.checkFormValidity());
  }

  private updateInvalidItems(event, alertUser = false) {
    this.determineAndSetInvalidItems();
    if (alertUser) {
      if (this.invalidItems?.length > 0) {
        this._eventService.error(this.message['error']['form-invalid']);
      } else {
        this._eventService.success(this.message['success']['form-valid']);
      }
    }
  }

  public emitInvalid(event) {
    this.invalid.emit(event);
  }

  // Formio (change) callback
  public onChange(event) {
    if (event?.changed?.component?.type === 'file') {
      // Save immediately using the current event, not the previous one: _changeValueSubject
      // is only updated further down, so reading it here would save stale (or, on the very
      // first file upload in a session, no) data and silently drop the file's reference.
      if (event.data) {
        this.updateInvalidItems(event);
        this.save.emit(event);
        this.fileSave.emit(event);
      }
    }
    if (event.data) {
      this.updatedForm['data'] = event.data;
      this.dataChanged.emit(this.updatedForm);
    }

    if (this.isInitialUpdate) {
      this.isInitialUpdate = false;
    } else {
      // no changes, no saving
      if (event.changed && event.changed.value) {
        // CKEditor returns '<p>&nbsp;</p>' when you removed all text.
        if (event.changed.value === '<p>&nbsp;</p>' || // CKEditor's empty
          event.changed.value === '<p><br></p>') {   // Quill's empty
          event.changed.value = '';
        }
        if (!this.readOnly) {
          this.changed.emit(true);
          this._changeValueSubject.next(event);
        }
      }
    }
  }

  private applyDisabledStateToButtons(disabled: boolean) {
    const saveButtons = this.formioComponent.formio.getComponents().filter(
      ({ component }) => component.type === 'button' && component.key === 'submit' && component.label === 'Save'
    );

    saveButtons.forEach((button) => {
      button.component.disabled = disabled;
      button.redraw();
    });
  }

  private removeWhiteSpace(data) {
    const newData = data;
    Object.keys(newData).forEach(key => {
      if (newData[key] === '<p>&nbsp;</p>' || // CKEditor's empty
        newData[key] === '<p><br></p>') {   // Quill's empty
        newData[key] = '';
      }
    });
    return newData;
  }
}
